<template>
  <BaseModal
    :modal-width="955"
    background-color="#222429"
    title-color="white"
    close-button-color="white"
    without-header-border
    @on-close-modal="onCloseModal"
  >
    <div class="autonomic-subscribe">
      <div class="autonomic-subscribe__content">
        <span class="autonomic-subscribe__line"></span>
        <h3 class="autonomic-subscribe__title">
          {{ _T(autonomicData.title) }}
        </h3>

        <div class="autonomic-subscribe__container">
          <p
            v-for="(description, index) in autonomicData.description"
            :key="index"
            class="autonomic-subscribe__description"
          >
            {{ _T(description) }}
          </p>
        </div>
      </div>
      <MyForm
        id="subscription"
        name="subscription"
        :state="form"
        :schema="schema"
        class="autonomic-subscribe__form"
        @submit="onSubscribe"
      >
        <div class="autonomic-subscribe__inputs">
          <MyInput
            v-model.trim="form.name"
            name="name"
            caption="@Name"
            autocomplete="given-name"
          />
          <MyInput
            v-model.trim="form.phone"
            v-maska="'+38(0##) ### ## ##'"
            name="phone"
            type="tel"
            caption="@Phone number"
            autocomplete="phone"
          />
          <MyInput
            v-model.trim="form.email"
            name="email"
            type="email"
            caption="E-mail"
            autocomplete="email"
          />
        </div>

        <div class="autonomic-subscribe__checkbox-wrp">
          <MyCheckbox
            v-model="form.isAgreementAccepted"
            name="isAgreementAccepted"
          >
            <span>
              {{ _T("@I agree with") }}
              <MyLink
                class="autonomic-subscribe__caption-link"
                :name="'privacy-policy'"
              >
                {{ _T("@privacy policy") }}
              </MyLink>
            </span>
          </MyCheckbox>
          <ButtonPrimary
            type="submit"
            :aria-label="_T('@Send')"
            :disabled="isHandled"
          >
            <span>{{ _T("@Send") }}</span>
            <img src="/img/arrow_right_light.svg" alt="arrow" />
          </ButtonPrimary>
        </div>
      </MyForm>
    </div>
  </BaseModal>
</template>

<script setup>
import {
  autonomicSubscriptionModal,
  successfulModal,
  useModalStore,
} from "~/modules/shared/modals/store/modalStore";
import { useUserStore } from "~/store/userStore";
import { useSingletonHandler } from "~/uses/useSingletonHandler";
import { useAPI } from "~/uses/useMyFetch";
import BaseModal from "~/modules/shared/modals/BaseModal.vue";
import ButtonPrimary from "~/modules/shared/buttons/ButtonPrimary.vue";
import MyForm from "~/nuxt_modules/common/ui/components/form/MyForm.vue";
import MyInput from "~/nuxt_modules/common/ui/components/form/MyInput.vue";
import { vMaska } from "maska/vue";
import MyCheckbox from "~/nuxt_modules/common/ui/components/form/MyCheckbox.vue";
import { z } from "zod";
import { phoneCheck } from "~/utils/phoneHelper.js";

const modalStore = useModalStore();
const userStore = useUserStore();

const { currentUser: user } = storeToRefs(userStore);

onMounted(() => {
  if (null !== user.value) {
    form.name = user.value.name.firstName;
    form.email = user.value.email;
  }
});

const successfulData = {
  title: _T("@Successful modal title"),
  description: _T("@Successful modal text"),
  isWithTimeout: true,
};

const autonomicData = {
  title: "@Want to know more about Autonomic's capabilities",
  description: [
    "@Get a free consultation",
    "@Our manager will contact you and answer all questions.",
  ],
};

const schema = z.object({
  name: z
    .string(_T("@Entry field required"))
    .max(255, `${_T("@Maximum field length")} 255`)
    .regex(/^\S*$/, _T("@Must not contain spaces"))
    .trim(),
  phone: z
    .string(_T("@Entry field required"))
    .refine(
      (phone) => phoneCheck(phone),
      _T("@You entered an incorrect phone number"),
    )
    .trim(),
  email: z
    .email(_T("@You entered an incorrect email"))
    .max(254, `${_T("@Maximum field length")} 254`)
    .trim(),
  isAgreementAccepted: z.literal(
    true,
    _T("@You must accept the terms and conditions"),
  ),
});

const form = reactive({
  name: undefined,
  phone: undefined,
  email: undefined,
  isAgreementAccepted: true,
});

const { isHandled, handle: onSubscribe } = useSingletonHandler(async (data) => {
  await useAPI("/marketing/event", {
    method: "POST",
    body: {
      event: "autonomicLandingRequestCreated",
      email: data.email,
      isConfirmed: data.isAgreementAccepted,
      name: data.name,
      phone: data.phone,
    },
  }).then(() => {
    modalStore.toggleModal(autonomicSubscriptionModal);

    modalStore.toggleModal(successfulModal, successfulData);
  });
});

const onCloseModal = () => {
  modalStore.toggleModal(autonomicSubscriptionModal);
};
</script>

<style lang="scss" scoped>
.autonomic-subscribe {
  @include flex-container(row, space-between, center);
  gap: 57px;

  @include mobile {
    @include flex-container(column, center, center);
  }

  &__content {
    max-width: 390px;
    width: 100%;

    @include flex-container(column);
    gap: 16px;

    @include mobile {
      align-items: center;
      text-align: center;
    }
  }

  &__title {
    font-family: "JWH V1", sans-serif;
    @include font(20, 24, 400);
    letter-spacing: 0.2px;

    @include mobile {
      @include font(18, 22, 400);
      letter-spacing: 0.18px;
    }
  }

  &__description {
    max-width: 360px;
    @include font(16, 23, 400);
    letter-spacing: 0.32px;
    color: #e9e9e9;

    @include mobile {
      @include font(14, 20, 400);
      letter-spacing: 0.28px;
    }
  }

  &__line {
    width: 80px;

    border: 1px solid var(--color-primary-base);

    @include bigMobile {
      width: 60px;
    }
  }

  &__form {
    width: 100%;
    @include flex-container(column, space-between);

    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.5);

    background: linear-gradient(
      117deg,
      rgba(243, 243, 243, 0.3) 24.93%,
      rgba(243, 243, 243, 0.2) 101.84%
    );
    box-shadow: 0 1px 25px 0 rgba(0, 0, 0, 0.05);

    gap: 24px;
    padding: 16px 24px;

    @include mobile {
      max-width: max-content;

      padding: 16px;
    }
  }

  &__inputs {
    @include flex-container(column, space-between, center);

    gap: 24px;
  }

  &__checkbox-wrp {
    width: min(100%, 363px);
    align-self: center;

    @include flex-container(column, center, center);
    gap: 24px;
  }
}
</style>
